'use client';

import { Button } from '@/components/ui/button';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight } from 'lucide-react';

export function MarketingHero() {
  return (
    <section id="marketing-hero-section" className="relative min-h-[85vh] flex items-center justify-center px-4 py-32 md:py-40 border-b border-stone/30">
      {/* Base stone/parchment background - clearly distinguishable from white */}
      <div id="hero-base-background" className="absolute inset-0 bg-[hsl(45,25%,92%)] z-0" />
      
      {/* Warm overlays - behind the image for subtle tone */}
      <div id="hero-overlays-container" className="absolute inset-0 pointer-events-none z-[1]">
        {/* Warm stone overlay with subtle gold tint - reduced opacity */}
        <div id="hero-stone-overlay" className="absolute inset-0 bg-gradient-to-b from-[hsl(45,25%,93%)]/60 to-[hsl(45,25%,90%)]/70" />
        {/* Warm background treatment - stone to gold gradient */}
        <div id="hero-gradient-overlay" className="absolute inset-0 bg-gradient-to-b from-[hsl(45,25%,92%)] via-[hsl(45,25%,91%)] to-[hsl(45,25%,90%)]" />
        {/* Radial gold glow - reduced opacity */}
        <div id="hero-radial-glow" className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[hsl(var(--gold)/0.12)] via-[hsl(var(--gold)/0.05)] to-transparent" />
      </div>

      {/* Church hero image - on top of overlays so it's visible */}
      <div id="hero-background-image-container" className="absolute inset-0 pointer-events-none overflow-hidden z-[2]">
        <div id="hero-background-image" className="relative w-full h-full opacity-[0.2] blur-sm">
          <Image
            src="/church-hero.png"
            alt=""
            fill
            className="object-cover"
            priority
            unoptimized
          />
        </div>
      </div>
      
      <div className="relative z-10 max-w-4xl mx-auto text-center space-y-10 md:space-y-12">
        <div className="relative">
          {/* Subtle WayMaker compass/star watermark behind headline */}
          {/* TODO: Replace inline SVG with /marketing/waymaker-watermark.svg when final asset is available */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none -z-10">
            {/* Placeholder inline SVG - will be replaced with /marketing/waymaker-watermark.svg */}
            <svg 
              width="200" 
              height="200" 
              viewBox="0 0 200 200" 
              className="opacity-[0.08] w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48"
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Compass circle */}
              <circle cx="100" cy="100" r="80" stroke="hsl(var(--gold))" strokeWidth="1.5" opacity="0.4" />
              {/* Compass cross */}
              <line x1="100" y1="20" x2="100" y2="180" stroke="hsl(var(--gold))" strokeWidth="1.5" opacity="0.3" />
              <line x1="20" y1="100" x2="180" y2="100" stroke="hsl(var(--gold))" strokeWidth="1.5" opacity="0.3" />
              {/* Star in center */}
              <path 
                d="M100 40 L110 70 L140 70 L115 90 L125 120 L100 100 L75 120 L85 90 L60 70 L90 70 Z" 
                fill="hsl(var(--gold))"
                opacity="0.5"
              />
            </svg>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-spectral font-semibold tracking-tight leading-[1.15] relative z-10">
            Every church has a story. <br /> Tell yours.
          </h1>
        </div>
        <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto font-normal leading-relaxed">
          Waymaker helps churches map their sacred spaces and share the meaning, history, and artistry behind every work—so parishioners and visitors can explore with deeper understanding and connection.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
          <Link href="/m/pricing">
            <Button 
              size="lg" 
              className="text-base px-8 bg-gold text-charcoal hover:bg-[hsl(var(--gold)/0.9)] border border-gold/40 shadow-soft hover:shadow-elegant"
            >
              Get Started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link href="/m/contact">
            <Button 
              size="lg" 
              variant="outline"
              className="text-base px-8 border-gold/40 text-gold hover:bg-gold/10 hover:text-gold hover:border-gold/60"
            >
              Learn More
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
